import { Link } from "react-router-dom";
import { Seo } from "@/seo/Seo";
import { Container } from "@/components/common/Container";
import { Section } from "@/components/common/Section";
import { SectionHeading } from "@/components/common/SectionHeading";
import { Reveal } from "@/components/common/Reveal";
import { Badge } from "@/components/ui/badge";
import { ArticleCard } from "@/components/insights/ArticleCard";
import { DetailCta } from "@/components/detail/DetailCta";
import { getRelatedArticles } from "@/data/articles.data";
import { cn } from "@/lib/utils";
import { ArrowLeft, Clock, Calendar, Tag } from "@/icons";
import type { Article } from "@/types/content";

export function ArticleTemplate({ data }: { data: Article }) {
  const related = getRelatedArticles(data.slug);

  return (
    <>
      <Seo
        title={data.title}
        description={data.seoDescription}
        path={`/insights/${data.slug}`}
        type="article"
      />

      {/* Cover hero */}
      <section className="relative overflow-hidden pt-28 sm:pt-32">
        <div className={cn("relative h-64 sm:h-80", "")}>
          <div className={cn("absolute inset-0 bg-gradient-to-br", data.cover)} />
          <div className="absolute inset-0 bg-grid-light opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent" />
        </div>
      </section>

      <Section spacing="compact" className="pt-0">
        <Container>
          <div className="mx-auto -mt-24 max-w-3xl">
            <Reveal>
              <Link
                to="/insights"
                data-testid="article-back"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-primary"
              >
                <ArrowLeft className="h-4 w-4" /> All insights
              </Link>
            </Reveal>

            <Reveal delay={0.05}>
              <div className="mt-6 flex flex-wrap items-center gap-3 text-xs">
                <span className="rounded-full bg-primary/10 px-3 py-1 font-semibold uppercase tracking-[0.12em] text-primary">
                  {data.category}
                </span>
                <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                  <Calendar className="h-3.5 w-3.5" /> {data.date}
                </span>
                <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                  <Clock className="h-3.5 w-3.5" /> {data.readingTime}
                </span>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <h1 className="mt-5 font-heading text-3xl font-extrabold leading-[1.12] tracking-tight text-foreground sm:text-4xl lg:text-[2.75rem]">
                {data.title}
              </h1>
            </Reveal>

            <Reveal delay={0.15}>
              <p className="mt-5 text-lg leading-relaxed text-muted-foreground">
                {data.excerpt}
              </p>
            </Reveal>

            {/* Author */}
            <Reveal delay={0.2}>
              <div className="mt-8 flex items-center gap-3 border-y border-border py-5">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 font-heading text-sm font-bold text-primary">
                  {data.author.initials}
                </span>
                <div>
                  <p className="text-sm font-semibold text-foreground">{data.author.name}</p>
                  <p className="text-xs text-muted-foreground">{data.author.role}</p>
                </div>
              </div>
            </Reveal>

            {/* Body */}
            <article className="mt-10 space-y-10" data-testid="article-body">
              {data.body.map((block, i) => (
                <Reveal key={i} delay={0.05}>
                  <div>
                    {block.heading && (
                      <h2 className="font-heading text-xl font-bold text-foreground sm:text-2xl">
                        {block.heading}
                      </h2>
                    )}
                    <div className={cn("space-y-4", block.heading && "mt-4")}>
                      {block.paragraphs.map((p, j) => (
                        <p key={j} className="text-[17px] leading-relaxed text-muted-foreground">
                          {p}
                        </p>
                      ))}
                    </div>
                  </div>
                </Reveal>
              ))}
            </article>

            {/* Tags */}
            <div className="mt-10 flex flex-wrap items-center gap-2 border-t border-border pt-8">
              <Tag className="h-4 w-4 text-muted-foreground" />
              {data.tags.map((t) => (
                <Badge
                  key={t}
                  variant="secondary"
                  className="rounded-full bg-muted font-medium text-muted-foreground"
                >
                  {t}
                </Badge>
              ))}
            </div>
          </div>
        </Container>
      </Section>

      {/* Related */}
      {related.length > 0 && (
        <Section spacing="compact" className="bg-card/40">
          <Container>
            <SectionHeading eyebrow="Keep reading" title="Related articles" />
            <div className="mt-10 grid gap-6 md:grid-cols-2">
              {related.map((a) => (
                <ArticleCard key={a.slug} article={a} />
              ))}
            </div>
          </Container>
        </Section>
      )}

      <DetailCta note="Have a project in mind? Let's turn these ideas into your product." />
    </>
  );
}
